import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Progress } from "../../components/ui/progress";
import { 
  User, Calendar, DollarSign, FileText, CheckCircle, Clock, 
  Building2, Phone, MapPin, CreditCard, AlertCircle, Upload, File, XCircle, Landmark, Trash2, Plus
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function EmployeeSelfService() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [timeOff, setTimeOff] = useState({ requests: [], balances: { pto: 0, sick: 0 } });
  const [paystubs, setPaystubs] = useState([]);
  const [onboarding, setOnboarding] = useState({ tasks: [], progress: { completed: 0, total: 0, percentage: 0 } });
  const [hasHRProfile, setHasHRProfile] = useState(true);
  const [documents, setDocuments] = useState({ requirements: [], completion: { total: 0, completed: 0, percentage: 0 } });
  const [i9Form, setI9Form] = useState(null);
  const [w4Form, setW4Form] = useState(null);
  const [bankAccounts, setBankAccounts] = useState({ accounts: [], settings: {} });
  
  const [activeTab, setActiveTab] = useState("profile");
  const [timeOffDialogOpen, setTimeOffDialogOpen] = useState(false);
  const [paystubDialogOpen, setPaystubDialogOpen] = useState(false);
  const [selectedPaystub, setSelectedPaystub] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [i9DialogOpen, setI9DialogOpen] = useState(false);
  const [w4DialogOpen, setW4DialogOpen] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  
  const [timeOffForm, setTimeOffForm] = useState({
    type: "pto", start_date: "", end_date: "", hours: 8, reason: ""
  });
  
  const [profileForm, setProfileForm] = useState({
    emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relationship: "",
    address: "", city: "", state: "", zip_code: "",
    bank_name: "", bank_account_number: "", bank_routing_number: ""
  });

  const [bankForm, setBankForm] = useState({
    account_holder_name: "", account_type: "checking", routing_number: "", account_number: "", is_primary: true
  });

  const [i9FormData, setI9FormData] = useState({
    last_name: "", first_name: "", middle_initial: "", other_last_names: "",
    address: "", apartment: "", city: "", state: "", zip_code: "",
    date_of_birth: "", ssn: "", email: "", phone: "",
    citizenship_status: "citizen", alien_number: "", i94_number: "", foreign_passport: "", expiration_date: ""
  });

  const [w4FormData, setW4FormData] = useState({
    first_name: "", last_name: "", ssn: "", address: "", city: "", state: "", zip_code: "",
    filing_status: "single", multiple_jobs_or_spouse_works: false,
    dependents_under_17: 0, other_dependents: 0,
    other_income: 0, deductions: 0, extra_withholding: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const [profileRes, timeOffRes, paystubsRes, onboardingRes, docsRes, i9Res, w4Res, bankRes] = await Promise.all([
        axios.get(`${API}/hr/self-service/profile`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        axios.get(`${API}/hr/self-service/time-off`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { requests: [], balances: { pto: 0, sick: 0 } } })),
        axios.get(`${API}/hr/self-service/paystubs`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/hr/self-service/onboarding`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { tasks: [], progress: { completed: 0, total: 0, percentage: 0 } } })),
        axios.get(`${API}/hr/self-service/documents`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { requirements: [], completion: { total: 0, completed: 0, percentage: 0 } } })),
        axios.get(`${API}/hr/self-service/i9`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/hr/self-service/w4`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/hr/self-service/bank-accounts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { accounts: [], settings: {} } }))
      ]);
      
      if (profileRes?.data) {
        setProfile(profileRes.data);
        setProfileForm({
          emergency_contact_name: profileRes.data.emergency_contact_name || "",
          emergency_contact_phone: profileRes.data.emergency_contact_phone || "",
          emergency_contact_relationship: profileRes.data.emergency_contact_relationship || "",
          address: profileRes.data.address || "",
          city: profileRes.data.city || "",
          state: profileRes.data.state || "",
          zip_code: profileRes.data.zip_code || "",
          bank_name: profileRes.data.bank_name || "",
          bank_account_number: profileRes.data.bank_account_number || "",
          bank_routing_number: profileRes.data.bank_routing_number || ""
        });
        setHasHRProfile(true);
      } else {
        setHasHRProfile(false);
      }
      
      setTimeOff(timeOffRes.data);
      setPaystubs(paystubsRes.data);
      setOnboarding(onboardingRes.data);
      setDocuments(docsRes.data);
      setI9Form(i9Res.data);
      setW4Form(w4Res.data);
      setBankAccounts(bankRes.data);
      
      // Pre-fill forms if data exists
      if (i9Res.data && Object.keys(i9Res.data).length > 0) {
        setI9FormData(prev => ({ ...prev, ...i9Res.data }));
      }
      if (w4Res.data && Object.keys(w4Res.data).length > 0) {
        setW4FormData(prev => ({ ...prev, ...w4Res.data }));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/hr/self-service/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated");
      setEditMode(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const handleRequestTimeOff = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/self-service/time-off`, timeOffForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Time off request submitted");
      setTimeOffDialogOpen(false);
      setTimeOffForm({ type: "pto", start_date: "", end_date: "", hours: 8, reason: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    }
  };

  const handleUploadDocument = async (requirementId, file) => {
    const token = localStorage.getItem("agent_token");
    const formData = new FormData();
    formData.append("file", file);
    
    setUploadingDoc(requirementId);
    try {
      await axios.post(`${API}/hr/self-service/documents/upload?requirement_id=${requirementId}`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      toast.success("Document uploaded successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleFileSelect = (e, requirementId) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUploadDocument(requirementId, file);
    }
    e.target.value = "";
  };

  const handleSaveI9 = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/self-service/i9`, i9FormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("I-9 form saved successfully");
      setI9DialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save I-9 form");
    }
  };

  const handleSaveW4 = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/self-service/w4`, w4FormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("W-4 form saved successfully");
      setW4DialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save W-4 form");
    }
  };

  const handleAddBankAccount = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/self-service/bank-accounts`, bankForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bank account added successfully");
      setBankDialogOpen(false);
      setBankForm({ account_holder_name: "", account_type: "checking", routing_number: "", account_number: "", is_primary: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add bank account");
    }
  };

  const handleDeleteBankAccount = async (accountId) => {
    if (!confirm("Remove this bank account?")) return;
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/hr/self-service/bank-accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bank account removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove bank account");
    }
  };

  const viewPaystub = async (paystubId) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/hr/self-service/paystubs/${paystubId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPaystub(response.data);
      setPaystubDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load paystub");
    }
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AgentLayout>
    );
  }

  if (!hasHRProfile) {
    return (
      <AgentLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">HR Profile Not Found</h2>
          <p className="text-muted-foreground">Your HR profile hasn't been set up yet. Please contact your administrator.</p>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>Employee Self Service</h1>
          <p className="text-muted-foreground">Manage your HR information, time off, and view paystubs</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-organic bg-blue-50">
            <CardContent className="p-4">
              <Calendar className="h-5 w-5 text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-blue-800">{timeOff.balances.pto}h</p>
              <p className="text-xs text-blue-600">PTO Balance</p>
            </CardContent>
          </Card>
          <Card className="card-organic bg-amber-50">
            <CardContent className="p-4">
              <Clock className="h-5 w-5 text-amber-600 mb-2" />
              <p className="text-2xl font-bold text-amber-800">{timeOff.balances.sick}h</p>
              <p className="text-xs text-amber-600">Sick Leave</p>
            </CardContent>
          </Card>
          <Card className="card-organic bg-green-50">
            <CardContent className="p-4">
              <FileText className="h-5 w-5 text-green-600 mb-2" />
              <p className="text-2xl font-bold text-green-800">{paystubs.length}</p>
              <p className="text-xs text-green-600">Paystubs</p>
            </CardContent>
          </Card>
          <Card className="card-organic">
            <CardContent className="p-4">
              <CheckCircle className="h-5 w-5 text-primary mb-2" />
              <p className="text-2xl font-bold">{onboarding.progress.percentage}%</p>
              <p className="text-xs text-muted-foreground">Onboarding</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap">
            <TabsTrigger value="profile">My Profile</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="direct-deposit">Direct Deposit</TabsTrigger>
            <TabsTrigger value="time-off">Time Off</TabsTrigger>
            <TabsTrigger value="paystubs">Paystubs</TabsTrigger>
            <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-4">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Employment Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Employment Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Department</Label>
                      <p className="font-medium">{profile?.department || "-"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Job Title</Label>
                      <p className="font-medium">{profile?.job_title || "-"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Employment Type</Label>
                      <p className="font-medium capitalize">{profile?.employment_type?.replace("_", " ") || "-"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Hire Date</Label>
                      <p className="font-medium">{profile?.hire_date || "-"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Manager</Label>
                      <p className="font-medium">{profile?.manager?.full_name || "-"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Employee ID</Label>
                      <p className="font-medium">{profile?.employee_id || "-"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Personal Info */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Personal Information
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setEditMode(!editMode)}>
                    {editMode ? "Cancel" : "Edit"}
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {editMode ? (
                    <>
                      <div className="space-y-2">
                        <Label>Address</Label>
                        <Input value={profileForm.address} onChange={(e) => setProfileForm(p => ({ ...p, address: e.target.value }))} />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-2">
                          <Label>City</Label>
                          <Input value={profileForm.city} onChange={(e) => setProfileForm(p => ({ ...p, city: e.target.value }))} />
                        </div>
                        <div className="space-y-2">
                          <Label>State</Label>
                          <Input value={profileForm.state} onChange={(e) => setProfileForm(p => ({ ...p, state: e.target.value }))} />
                        </div>
                        <div className="space-y-2">
                          <Label>ZIP</Label>
                          <Input value={profileForm.zip_code} onChange={(e) => setProfileForm(p => ({ ...p, zip_code: e.target.value }))} />
                        </div>
                      </div>
                      <Button onClick={handleUpdateProfile} className="w-full">Save Changes</Button>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>{profile?.address ? `${profile.address}, ${profile.city}, ${profile.state} ${profile.zip_code}` : "Not provided"}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Emergency Contact */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Emergency Contact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {editMode ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Contact Name</Label>
                        <Input value={profileForm.emergency_contact_name} onChange={(e) => setProfileForm(p => ({ ...p, emergency_contact_name: e.target.value }))} />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input value={profileForm.emergency_contact_phone} onChange={(e) => setProfileForm(p => ({ ...p, emergency_contact_phone: e.target.value }))} />
                        </div>
                        <div className="space-y-2">
                          <Label>Relationship</Label>
                          <Input value={profileForm.emergency_contact_relationship} onChange={(e) => setProfileForm(p => ({ ...p, emergency_contact_relationship: e.target.value }))} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="font-medium">{profile?.emergency_contact_name || "Not provided"}</p>
                      {profile?.emergency_contact_phone && <p className="text-sm text-muted-foreground">{profile.emergency_contact_phone}</p>}
                      {profile?.emergency_contact_relationship && <p className="text-sm text-muted-foreground">{profile.emergency_contact_relationship}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Bank Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Direct Deposit
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {editMode ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Bank Name</Label>
                        <Input value={profileForm.bank_name} onChange={(e) => setProfileForm(p => ({ ...p, bank_name: e.target.value }))} />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Account Number</Label>
                          <Input type="password" value={profileForm.bank_account_number} onChange={(e) => setProfileForm(p => ({ ...p, bank_account_number: e.target.value }))} />
                        </div>
                        <div className="space-y-2">
                          <Label>Routing Number</Label>
                          <Input value={profileForm.bank_routing_number} onChange={(e) => setProfileForm(p => ({ ...p, bank_routing_number: e.target.value }))} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="font-medium">{profile?.bank_name || "Not provided"}</p>
                      {profile?.bank_account_number && <p className="text-sm text-muted-foreground">Account: ****{profile.bank_account_number.slice(-4)}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-4">
            <div className="space-y-6">
              {/* Progress Overview */}
              <Card className="card-organic bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm text-blue-700 font-medium">Document Completion</p>
                      <p className="text-2xl font-bold text-blue-900">{documents.completion?.completed || 0} of {documents.completion?.total || 0} required</p>
                    </div>
                    <FileText className="h-10 w-10 text-blue-600 opacity-50" />
                  </div>
                  <Progress value={documents.completion?.percentage || 0} className="h-2" />
                </CardContent>
              </Card>

              {/* Tax Forms Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Tax Forms</CardTitle>
                  <CardDescription>Complete your I-9 and W-4 tax forms</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">I-9</Badge>
                          <span className="font-medium">Employment Eligibility</span>
                        </div>
                        {i9Form?.status && (
                          <Badge className={i9Form.status === "verified" ? "bg-green-100 text-green-800" : i9Form.status === "rejected" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}>
                            {i9Form.status}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">Required form to verify your eligibility to work in the US</p>
                      <Button onClick={() => setI9DialogOpen(true)} className="w-full" variant={i9Form?.form_id ? "outline" : "default"} data-testid="fill-i9-btn">
                        {i9Form?.form_id ? "Edit I-9 Form" : "Complete I-9 Form"}
                      </Button>
                    </div>
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">W-4</Badge>
                          <span className="font-medium">Tax Withholding</span>
                        </div>
                        {w4Form?.status && (
                          <Badge className="bg-green-100 text-green-800">{w4Form.status}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">Federal tax withholding certificate</p>
                      <Button onClick={() => setW4DialogOpen(true)} className="w-full" variant={w4Form?.form_id ? "outline" : "default"} data-testid="fill-w4-btn">
                        {w4Form?.form_id ? "Edit W-4 Form" : "Complete W-4 Form"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Document Upload Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Required Documents</CardTitle>
                  <CardDescription>Upload the following documents to complete your onboarding</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {documents.requirements?.sort((a, b) => a.order - b.order).map((req) => (
                      <div key={req.requirement_id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="capitalize">{req.document_type}</Badge>
                            <div>
                              <p className="font-medium">{req.name}</p>
                              <p className="text-sm text-muted-foreground">{req.description}</p>
                            </div>
                          </div>
                          {req.required && <Badge variant="secondary">Required</Badge>}
                        </div>
                        {req.uploaded ? (
                          <div className="bg-muted/50 p-3 rounded-md mt-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <File className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{req.filename}</span>
                              </div>
                              <Badge className={req.status === "approved" ? "bg-green-100 text-green-800" : req.status === "rejected" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}>
                                {req.status === "pending_review" ? "Pending Review" : req.status}
                              </Badge>
                            </div>
                            {req.status === "rejected" && (
                              <div className="mt-2">
                                <input type="file" className="hidden" id={`reupload-${req.requirement_id}`} onChange={(e) => handleFileSelect(e, req.requirement_id)} />
                                <label htmlFor={`reupload-${req.requirement_id}`}>
                                  <Button variant="outline" size="sm" className="cursor-pointer" asChild disabled={uploadingDoc === req.requirement_id}>
                                    <span>
                                      <Upload className="h-4 w-4 mr-1" />
                                      Re-upload
                                    </span>
                                  </Button>
                                </label>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="mt-3">
                            <input type="file" className="hidden" id={`upload-${req.requirement_id}`} onChange={(e) => handleFileSelect(e, req.requirement_id)} />
                            <label htmlFor={`upload-${req.requirement_id}`}>
                              <Button variant="outline" className="w-full cursor-pointer" asChild disabled={uploadingDoc === req.requirement_id}>
                                <span>
                                  {uploadingDoc === req.requirement_id ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="h-4 w-4 mr-2" />
                                      Upload Document
                                    </>
                                  )}
                                </span>
                              </Button>
                            </label>
                          </div>
                        )}
                      </div>
                    ))}
                    {(!documents.requirements || documents.requirements.length === 0) && (
                      <p className="text-center text-muted-foreground py-8">No document requirements configured</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Direct Deposit Tab */}
          <TabsContent value="direct-deposit" className="mt-4">
            <div className="space-y-6">
              {/* Status Card */}
              <Card className="card-organic bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-700 font-medium">Direct Deposit Status</p>
                      <p className="text-2xl font-bold text-green-800">
                        {bankAccounts.settings?.enabled ? "Enabled" : "Not Configured"}
                      </p>
                      <p className="text-sm text-green-600 mt-1">
                        {bankAccounts.accounts?.length || 0} bank account(s) on file
                      </p>
                    </div>
                    <Landmark className="h-12 w-12 text-green-600 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              {/* Bank Accounts */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Bank Accounts</CardTitle>
                    <CardDescription>Manage your bank accounts for direct deposit payments</CardDescription>
                  </div>
                  {(bankAccounts.accounts?.length || 0) < 2 && (
                    <Button onClick={() => setBankDialogOpen(true)} data-testid="add-bank-account-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Bank Account
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {bankAccounts.accounts?.map((account) => (
                      <div key={account.account_id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                              <Landmark className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{account.account_holder_name}</p>
                              <p className="text-sm text-muted-foreground capitalize">
                                {account.account_type} ••••{account.account_number_last4}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Routing: {account.routing_number}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {account.is_primary && (
                              <Badge className="bg-green-100 text-green-800">Primary</Badge>
                            )}
                            <Badge variant="outline" className={account.status === "verified" ? "border-green-500 text-green-600" : "border-yellow-500 text-yellow-600"}>
                              {account.status}
                            </Badge>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteBankAccount(account.account_id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(!bankAccounts.accounts || bankAccounts.accounts.length === 0) && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Landmark className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No bank accounts configured</p>
                        <p className="text-sm">Add a bank account to receive payments via direct deposit</p>
                        <Button onClick={() => setBankDialogOpen(true)} className="mt-4" data-testid="add-first-bank-btn">
                          <Plus className="h-4 w-4 mr-2" />
                          Add Bank Account
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Direct Deposit Info */}
              <Card>
                <CardHeader>
                  <CardTitle>How Direct Deposit Works</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mb-2 text-primary font-bold">1</div>
                      <p className="font-medium">Add Bank Account</p>
                      <p className="text-sm text-muted-foreground">Securely add your bank routing and account number</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mb-2 text-primary font-bold">2</div>
                      <p className="font-medium">Verification</p>
                      <p className="text-sm text-muted-foreground">Your account is verified automatically</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mb-2 text-primary font-bold">3</div>
                      <p className="font-medium">Get Paid</p>
                      <p className="text-sm text-muted-foreground">Payments are deposited directly to your account</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Time Off Tab */}
          <TabsContent value="time-off" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Time Off Requests</CardTitle>
                  <CardDescription>PTO: {timeOff.balances.pto}h available | Sick: {timeOff.balances.sick}h available</CardDescription>
                </div>
                <Button onClick={() => setTimeOffDialogOpen(true)} data-testid="request-time-off-btn">
                  <Calendar className="h-4 w-4 mr-2" />
                  Request Time Off
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {timeOff.requests.map((req) => (
                    <div key={req.request_id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <Badge variant={req.type === "pto" ? "default" : "secondary"}>{req.type.toUpperCase()}</Badge>
                        <div>
                          <p className="font-medium">{req.start_date} - {req.end_date}</p>
                          <p className="text-sm text-muted-foreground">{req.hours} hours • {req.reason || "No reason provided"}</p>
                        </div>
                      </div>
                      <Badge className={req.status === "approved" ? "bg-green-100 text-green-800" : req.status === "rejected" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}>
                        {req.status}
                      </Badge>
                    </div>
                  ))}
                  {timeOff.requests.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No time off requests yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Paystubs Tab */}
          <TabsContent value="paystubs" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Paystubs</CardTitle>
                <CardDescription>View your pay history</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Pay Date</th>
                        <th>Pay Period</th>
                        <th>Hours</th>
                        <th>Gross</th>
                        <th>Net</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paystubs.map((stub) => (
                        <tr key={stub.paystub_id}>
                          <td>{new Date(stub.pay_date).toLocaleDateString()}</td>
                          <td>{stub.pay_period_start} - {stub.pay_period_end}</td>
                          <td>{stub.regular_hours + stub.overtime_hours}h</td>
                          <td>${stub.gross_pay?.toFixed(2)}</td>
                          <td className="font-semibold">${stub.net_pay?.toFixed(2)}</td>
                          <td>
                            <Button size="sm" variant="outline" onClick={() => viewPaystub(stub.paystub_id)}>
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {paystubs.length === 0 && (
                        <tr><td colSpan={6} className="text-center text-muted-foreground py-8">No paystubs available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Onboarding Tab */}
          <TabsContent value="onboarding" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Onboarding Progress</CardTitle>
                <CardDescription>{onboarding.progress.completed} of {onboarding.progress.total} tasks completed</CardDescription>
              </CardHeader>
              <CardContent>
                <Progress value={onboarding.progress.percentage} className="h-3 mb-6" />
                <div className="space-y-2">
                  {onboarding.tasks.sort((a, b) => (a.task?.order || 0) - (b.task?.order || 0)).map((task) => (
                    <div key={task.task_id} className="flex items-center gap-3 p-3 border rounded-lg">
                      {task.completed ? (
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <div className="w-5 h-5 border-2 rounded-full flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className={task.completed ? "line-through text-muted-foreground" : "font-medium"}>{task.task?.title}</p>
                        <p className="text-sm text-muted-foreground">{task.task?.description}</p>
                      </div>
                      <Badge variant="outline">{task.task?.category}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Time Off Request Dialog */}
      <Dialog open={timeOffDialogOpen} onOpenChange={setTimeOffDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Time Off</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={timeOffForm.type} onValueChange={(v) => setTimeOffForm(p => ({ ...p, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pto">PTO ({timeOff.balances.pto}h available)</SelectItem>
                  <SelectItem value="sick">Sick Leave ({timeOff.balances.sick}h available)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={timeOffForm.start_date} onChange={(e) => setTimeOffForm(p => ({ ...p, start_date: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={timeOffForm.end_date} onChange={(e) => setTimeOffForm(p => ({ ...p, end_date: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Hours</Label>
              <Input type="number" value={timeOffForm.hours} onChange={(e) => setTimeOffForm(p => ({ ...p, hours: parseFloat(e.target.value) }))} />
            </div>
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea value={timeOffForm.reason} onChange={(e) => setTimeOffForm(p => ({ ...p, reason: e.target.value }))} placeholder="Brief description..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTimeOffDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRequestTimeOff}>Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paystub Detail Dialog */}
      <Dialog open={paystubDialogOpen} onOpenChange={setPaystubDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Paystub Details</DialogTitle>
          </DialogHeader>
          {selectedPaystub && (
            <div className="space-y-4">
              <div className="text-center border-b pb-4">
                <h3 className="text-lg font-bold">THERENTPAYER</h3>
                <p className="text-sm text-muted-foreground">Pay Statement</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Employee</Label>
                  <p className="font-medium">{selectedPaystub.employee?.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Pay Date</Label>
                  <p className="font-medium">{new Date(selectedPaystub.pay_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Pay Period</Label>
                  <p className="font-medium">{selectedPaystub.pay_period_start} - {selectedPaystub.pay_period_end}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Employee ID</Label>
                  <p className="font-medium">{selectedPaystub.employee?.employee_id}</p>
                </div>
              </div>
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>Regular Hours ({selectedPaystub.regular_hours}h)</span>
                  <span>-</span>
                </div>
                <div className="flex justify-between">
                  <span>Overtime Hours ({selectedPaystub.overtime_hours}h)</span>
                  <span>-</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span>Gross Pay</span>
                  <span>${selectedPaystub.gross_pay?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-red-600">
                  <span>Deductions</span>
                  <span>-${selectedPaystub.deductions?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Net Pay</span>
                  <span>${selectedPaystub.net_pay?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* I-9 Form Dialog */}
      <Dialog open={i9DialogOpen} onOpenChange={setI9DialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>I-9 Employment Eligibility Verification</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Section 1: Employee Information */}
            <div>
              <h3 className="font-semibold mb-3 text-lg">Section 1: Employee Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input value={i9FormData.last_name} onChange={(e) => setI9FormData(p => ({ ...p, last_name: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input value={i9FormData.first_name} onChange={(e) => setI9FormData(p => ({ ...p, first_name: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Middle Initial</Label>
                  <Input value={i9FormData.middle_initial} onChange={(e) => setI9FormData(p => ({ ...p, middle_initial: e.target.value }))} maxLength={1} />
                </div>
                <div className="space-y-2">
                  <Label>Other Last Names Used</Label>
                  <Input value={i9FormData.other_last_names} onChange={(e) => setI9FormData(p => ({ ...p, other_last_names: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 mt-4">
                <div className="col-span-2 space-y-2">
                  <Label>Street Address *</Label>
                  <Input value={i9FormData.address} onChange={(e) => setI9FormData(p => ({ ...p, address: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Apt #</Label>
                  <Input value={i9FormData.apartment} onChange={(e) => setI9FormData(p => ({ ...p, apartment: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input value={i9FormData.city} onChange={(e) => setI9FormData(p => ({ ...p, city: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>State *</Label>
                  <Input value={i9FormData.state} onChange={(e) => setI9FormData(p => ({ ...p, state: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>ZIP Code *</Label>
                  <Input value={i9FormData.zip_code} onChange={(e) => setI9FormData(p => ({ ...p, zip_code: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Date of Birth *</Label>
                  <Input type="date" value={i9FormData.date_of_birth} onChange={(e) => setI9FormData(p => ({ ...p, date_of_birth: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>SSN (last 4 digits)</Label>
                  <Input value={i9FormData.ssn} onChange={(e) => setI9FormData(p => ({ ...p, ssn: e.target.value }))} maxLength={4} placeholder="XXXX" />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input type="email" value={i9FormData.email} onChange={(e) => setI9FormData(p => ({ ...p, email: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input value={i9FormData.phone} onChange={(e) => setI9FormData(p => ({ ...p, phone: e.target.value }))} />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <Label>Citizenship / Immigration Status *</Label>
                <Select value={i9FormData.citizenship_status} onValueChange={(v) => setI9FormData(p => ({ ...p, citizenship_status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="citizen">A citizen of the United States</SelectItem>
                    <SelectItem value="noncitizen_national">A noncitizen national of the United States</SelectItem>
                    <SelectItem value="permanent_resident">A lawful permanent resident</SelectItem>
                    <SelectItem value="alien_authorized">An alien authorized to work</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(i9FormData.citizenship_status === "permanent_resident" || i9FormData.citizenship_status === "alien_authorized") && (
                <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-muted rounded-lg">
                  <div className="space-y-2">
                    <Label>Alien Registration Number / USCIS Number</Label>
                    <Input value={i9FormData.alien_number} onChange={(e) => setI9FormData(p => ({ ...p, alien_number: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>I-94 Admission Number</Label>
                    <Input value={i9FormData.i94_number} onChange={(e) => setI9FormData(p => ({ ...p, i94_number: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Foreign Passport Number</Label>
                    <Input value={i9FormData.foreign_passport} onChange={(e) => setI9FormData(p => ({ ...p, foreign_passport: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Work Authorization Expiration Date</Label>
                    <Input type="date" value={i9FormData.expiration_date} onChange={(e) => setI9FormData(p => ({ ...p, expiration_date: e.target.value }))} />
                  </div>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">By saving this form, I attest, under penalty of perjury, that I am aware that federal law provides for imprisonment and/or fines for false statements in connection with the completion of this form.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setI9DialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveI9} disabled={!i9FormData.first_name || !i9FormData.last_name || !i9FormData.address}>Save I-9 Form</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* W-4 Form Dialog */}
      <Dialog open={w4DialogOpen} onOpenChange={setW4DialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>W-4 Employee's Withholding Certificate</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Step 1: Personal Information */}
            <div>
              <h3 className="font-semibold mb-3">Step 1: Personal Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input value={w4FormData.first_name} onChange={(e) => setW4FormData(p => ({ ...p, first_name: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input value={w4FormData.last_name} onChange={(e) => setW4FormData(p => ({ ...p, last_name: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 mt-4">
                <div className="col-span-2 space-y-2">
                  <Label>Address *</Label>
                  <Input value={w4FormData.address} onChange={(e) => setW4FormData(p => ({ ...p, address: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input value={w4FormData.city} onChange={(e) => setW4FormData(p => ({ ...p, city: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>State *</Label>
                  <Input value={w4FormData.state} onChange={(e) => setW4FormData(p => ({ ...p, state: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>ZIP Code *</Label>
                  <Input value={w4FormData.zip_code} onChange={(e) => setW4FormData(p => ({ ...p, zip_code: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>SSN (last 4 digits)</Label>
                  <Input value={w4FormData.ssn} onChange={(e) => setW4FormData(p => ({ ...p, ssn: e.target.value }))} maxLength={4} placeholder="XXXX" />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <Label>Filing Status *</Label>
                <Select value={w4FormData.filing_status} onValueChange={(v) => setW4FormData(p => ({ ...p, filing_status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single or Married filing separately</SelectItem>
                    <SelectItem value="married_filing_jointly">Married filing jointly</SelectItem>
                    <SelectItem value="head_of_household">Head of household</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Step 2: Multiple Jobs */}
            <div>
              <h3 className="font-semibold mb-3">Step 2: Multiple Jobs or Spouse Works</h3>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="multiple_jobs" 
                  checked={w4FormData.multiple_jobs_or_spouse_works} 
                  onChange={(e) => setW4FormData(p => ({ ...p, multiple_jobs_or_spouse_works: e.target.checked }))}
                  className="h-4 w-4"
                />
                <Label htmlFor="multiple_jobs">Check if you have more than one job or your spouse also works</Label>
              </div>
            </div>

            {/* Step 3: Dependents */}
            <div>
              <h3 className="font-semibold mb-3">Step 3: Claim Dependents</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Children under 17 (x $2,000)</Label>
                  <Input type="number" value={w4FormData.dependents_under_17} onChange={(e) => setW4FormData(p => ({ ...p, dependents_under_17: parseInt(e.target.value) || 0 }))} />
                </div>
                <div className="space-y-2">
                  <Label>Other dependents (x $500)</Label>
                  <Input type="number" value={w4FormData.other_dependents} onChange={(e) => setW4FormData(p => ({ ...p, other_dependents: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>
              {(w4FormData.dependents_under_17 > 0 || w4FormData.other_dependents > 0) && (
                <p className="text-sm text-muted-foreground mt-2">
                  Total dependent credit: ${(w4FormData.dependents_under_17 * 2000) + (w4FormData.other_dependents * 500)}
                </p>
              )}
            </div>

            {/* Step 4: Other Adjustments */}
            <div>
              <h3 className="font-semibold mb-3">Step 4: Other Adjustments (Optional)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Other income ($)</Label>
                  <Input type="number" value={w4FormData.other_income} onChange={(e) => setW4FormData(p => ({ ...p, other_income: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div className="space-y-2">
                  <Label>Deductions ($)</Label>
                  <Input type="number" value={w4FormData.deductions} onChange={(e) => setW4FormData(p => ({ ...p, deductions: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div className="space-y-2">
                  <Label>Extra withholding ($)</Label>
                  <Input type="number" value={w4FormData.extra_withholding} onChange={(e) => setW4FormData(p => ({ ...p, extra_withholding: parseFloat(e.target.value) || 0 }))} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setW4DialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveW4} disabled={!w4FormData.first_name || !w4FormData.last_name || !w4FormData.address}>Save W-4 Form</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Bank Account Dialog */}
      <Dialog open={bankDialogOpen} onOpenChange={setBankDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Bank Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Account Holder Name *</Label>
              <Input 
                value={bankForm.account_holder_name} 
                onChange={(e) => setBankForm(p => ({ ...p, account_holder_name: e.target.value }))} 
                placeholder="John Doe" 
              />
            </div>
            <div className="space-y-2">
              <Label>Account Type *</Label>
              <Select value={bankForm.account_type} onValueChange={(v) => setBankForm(p => ({ ...p, account_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="checking">Checking</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Routing Number *</Label>
                <Input 
                  value={bankForm.routing_number} 
                  onChange={(e) => setBankForm(p => ({ ...p, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) }))} 
                  placeholder="9 digits" 
                  maxLength={9}
                />
              </div>
              <div className="space-y-2">
                <Label>Account Number *</Label>
                <Input 
                  value={bankForm.account_number} 
                  onChange={(e) => setBankForm(p => ({ ...p, account_number: e.target.value.replace(/\D/g, '') }))} 
                  placeholder="Account number"
                  type="password"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="is_primary" 
                checked={bankForm.is_primary} 
                onChange={(e) => setBankForm(p => ({ ...p, is_primary: e.target.checked }))} 
                className="h-4 w-4"
              />
              <Label htmlFor="is_primary">Set as primary account for direct deposits</Label>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
              <p className="font-medium mb-1">Security Notice</p>
              <p>Your bank information is encrypted and securely stored. We never share your financial details with third parties.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBankDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddBankAccount} 
              disabled={!bankForm.account_holder_name || bankForm.routing_number.length !== 9 || bankForm.account_number.length < 4}
            >
              Add Bank Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
